/** Hero tile photos under `public/images/WV/` — match on title/subtitle text. */
const WV_DEPT_TILE_HEALTH = '/images/WV/health.webp';
const WV_DEPT_TILE_REVENUE = '/images/WV/revenue.webp';
/**
 * Assigns WV landing hero tile photos when title or subtitle matches Health / Revenue cues.
 * Intended to run **after** {@link mergeDepartmentImagesFromPageJson} so CMS merge applies first,
 * then these defaults fill `health.webp` / `revenue.webp` for matching departments.
 */
export function applyWvLandingDepartmentHeroImages(departments) {
    return departments.map((d) => {
        const path = matchWvDepartmentHeroPath(d.title ?? '') ?? matchWvDepartmentHeroPath(d.subtitle ?? '');
        if (!path)
            return d;
        return { ...d, image: path };
    });
}
function matchWvDepartmentHeroPath(text) {
    const t = text.trim().toLowerCase();
    if (!t)
        return undefined;
    if (/\brevenue\b/.test(t))
        return WV_DEPT_TILE_REVENUE;
    if (/\b(state )?tax\b/.test(t) && /\b(department|division|office)\b/.test(t))
        return WV_DEPT_TILE_REVENUE;
    if (/\bhealth\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    if (/\bdhhr\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    if (/\bhuman services\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    if (/\bpublic health\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    if (/\bhospital\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    if (/\bbureau of health\b/.test(t))
        return WV_DEPT_TILE_HEALTH;
    return undefined;
}
export function permitsPathSegment(href) {
    if (!href || !href.includes('/permits/'))
        return '';
    try {
        return decodeURIComponent(href.split('/permits/')[1]?.split(/[?#]/)[0] ?? '').trim().toLowerCase();
    }
    catch {
        return '';
    }
}
/** Merge `landing.json` department entries onto API rows — restores local `/images/...` when the grid omits raster URLs. */
export function mergeDepartmentImagesFromPageJson(apiList, pageBlocks) {
    if (!pageBlocks?.length)
        return apiList;
    const findOverride = (d) => {
        const rid = (d.recordId ?? '').trim().toLowerCase();
        const seg = permitsPathSegment(d.href);
        const title = (d.title ?? '').trim().toLowerCase();
        for (const p of pageBlocks) {
            const pr = (p.recordId ?? '').trim().toLowerCase();
            if (rid && pr && rid === pr)
                return p;
        }
        for (const p of pageBlocks) {
            const ps = permitsPathSegment(p.href);
            if (seg && ps && seg === ps)
                return p;
        }
        if (title) {
            for (const p of pageBlocks) {
                if ((p.title ?? '').trim().toLowerCase() === title)
                    return p;
                const mt = p.matchTitles;
                if (Array.isArray(mt) && mt.some((m) => m && String(m).trim().toLowerCase() === title))
                    return p;
            }
        }
        return undefined;
    };
    return apiList.map((d) => {
        const o = findOverride(d);
        if (!o)
            return d;
        let image = d.image;
        if (typeof o.image === 'string') {
            image = o.image.trim() ? o.image.trim() : undefined;
        }
        const logoImage = typeof o.logoImage === 'string' && o.logoImage.trim() ? o.logoImage.trim() : d.logoImage;
        const imageAlt = o.imageAlt ?? d.imageAlt;
        const imageFit = o.imageFit ?? d.imageFit;
        const icon = d.icon ?? (typeof o.icon === 'string' && o.icon.trim() ? o.icon.trim() : undefined);
        const accentColor = d.accentColor ?? o.accentColor;
        return {
            ...d,
            image,
            logoImage,
            imageAlt,
            imageFit,
            icon,
            accentColor,
        };
    });
}
